import { useState } from 'react';
import AmbientBackground from '../common/AmbientBackground';
import PublicSomaticExperiment from './PublicSomaticExperiment';

const ProspectLanding = ({ onBack, navigate }) => {
    const [selectedDocId, setSelectedDocId] = useState(null);

    const docs = [
        { 
            id: 'counseling', 
            title: 'Cos\'è il Counseling?', 
            icon: '🌿', 
            description: 'Un percorso di consapevolezza e crescita personale basato sull\'ascolto e l\'esperienza.',
            detail: 'Scopri come il counseling può aiutarti a sbloccare il tuo potenziale.' 
        },
        {
            id: 'somatic',
            title: 'Bioenergeticamente Te',
            icon: '🧬',
            description: 'Il corpo racconta storie che la mente non sa ancora di avere. Prova questo breve esperimento.',
            detail: 'Nessun login richiesto. Scopri il tuo profilo caratteriale bioenergetico.',
            highlight: true
        },
        { 
            id: 'privacy', 
            title: 'Gestione Dati & Sicurezza', 
            icon: '🔒', 
            description: 'La tua privacy è la nostra priorità. Scopri come proteggiamo le tue informazioni.',
            detail: 'Trasparenza totale sul trattamento dei tuoi dati sensibili.' 
        }
    ];



    if (selectedDocId === 'counseling') {
        return (
            <div className="min-h-screen bg-slate-50 p-6 flex flex-col items-center animate-fade-in relative overflow-hidden">
                <AmbientBackground />
                <div className="max-w-4xl w-full bg-white rounded-3xl shadow-xl p-8 md:p-12 relative z-10 my-8">
                    <button onClick={() => setSelectedDocId(null)} className="absolute top-6 right-8 text-sm font-bold text-slate-400 hover:text-slate-800 transition">✕ Chiudi</button>
                    
                    <div className="prose prose-slate max-w-none">
                        <div className="flex items-center gap-6 mb-12 border-b pb-6">
                             <div className="w-20 h-20 bg-[#f0f5f8] rounded-3xl flex items-center justify-center text-4xl shadow-sm">🌿</div>
                             <div>
                                <h1 className="text-4xl font-black text-[#24485d] m-0">Cos'è il Counseling?</h1>
                                <p className="text-sm text-[#85a8bf] font-bold uppercase tracking-widest mt-1">Guida al percorso di consapevolezza</p>
                             </div>
                        </div>

                        <section className="mb-12">
                            <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3 italic">
                                <span className="bg-[#335d73] text-white w-8 h-8 rounded-full flex items-center justify-center text-xs not-italic shadow-sm">1</span>
                                Definizione e Ambito
                            </h2>
                            <p className="text-lg text-slate-700 leading-relaxed mb-6">
                                Il counseling si configura come una <strong>relazione d'aiuto professionale</strong>, finalizzata a sostenere la persona nella risoluzione di un problema specifico, nella chiarificazione di un disagio esistenziale o nel potenziamento della propria consapevolezza.
                            </p>
                            <p className="text-lg text-slate-700 leading-relaxed mb-6 font-medium italic text-[#24485d] bg-[#f0f5f8] p-6 rounded-2xl border border-[#d9e7ed] shadow-sm">
                                Il metodo <strong>BioGestalt®</strong> è un approccio integrato di counseling nato in Italia che unisce la <strong>Terapia della Gestalt</strong> (focalizzata sul "qui e ora" e la consapevolezza emotiva) e la <strong>Bioenergetica</strong> (centrata sul corpo e lo sblocco delle tensioni muscolari). Mira a riarmonizzare mente e corpo, migliorando la vitalità e le relazioni.
                            </p>
                            <p className="text-lg text-slate-700 leading-relaxed mb-6">
                                A differenza della psicoterapia, il counseling non interviene su patologie psichiche. L'obiettivo primario è aiutare l'individuo ad <strong>armonizzare la propria personalità</strong>, mettendola al servizio dei propri obiettivi e della propria felicità.
                            </p>
                            <p className="text-slate-600 bg-slate-50 p-4 rounded-xl text-sm border-l-4 border-[#85a8bf]">
                                Infine, il counselor lavora spesso in <strong>equipe multidisciplinari</strong> per supportare persone che necessitano di un sostegno integrato su diverse sfere della propria vita.
                            </p>
                        </section>

                        <section className="mb-12">
                             <h2 className="text-2xl font-bold text-slate-800 mb-8 flex items-center gap-3 italic">
                                <span className="bg-[#335d73] text-white w-8 h-8 rounded-full flex items-center justify-center text-xs not-italic shadow-sm">2</span>
                                Approcci Teorici Fondamentali
                             </h2>
                             <div className="grid md:grid-cols-2 gap-6">
                                 <div className="p-6 rounded-3xl border border-slate-100 bg-white shadow-sm hover:border-[#85a8bf]/30 hover:shadow-md transition">
                                     <div className="flex items-center gap-3 mb-4">
                                         <div className="w-10 h-10 bg-[#f0f5f8] rounded-xl flex items-center justify-center text-xl">👤</div>
                                         <h4 className="font-bold text-slate-800 uppercase tracking-tight text-sm">Terapia Centrata sul Cliente</h4>
                                     </div>
                                     <p className="text-sm text-slate-600 leading-relaxed">
                                        Basata su <strong>Empatia</strong>, <strong>Accettazione Incondizionata</strong> e <strong>Congruenza</strong>. Si fonda sulla convinzione che la persona possieda già in sé le risorse per il cambiamento.
                                     </p>
                                 </div>
                                 <div className="p-6 rounded-3xl border border-slate-100 bg-white shadow-sm hover:border-[#85a8bf]/30 hover:shadow-md transition">
                                     <div className="flex items-center gap-3 mb-4">
                                         <div className="w-10 h-10 bg-[#f0f5f8] rounded-xl flex items-center justify-center text-xl">👁️</div>
                                         <h4 className="font-bold text-slate-800 uppercase tracking-tight text-sm">La Gestalt</h4>
                                     </div>
                                     <p className="text-sm text-slate-600 leading-relaxed">
                                        Focus sul <strong>"Qui e Ora"</strong>. Osserva come il passato influenza il presente e utilizza tecniche attive per far emergere emozioni inespresse.
                                     </p>
                                 </div>
                                 <div className="p-6 rounded-3xl border border-slate-100 bg-white shadow-sm hover:border-[#85a8bf]/30 hover:shadow-md transition">
                                     <div className="flex items-center gap-3 mb-4">
                                         <div className="w-10 h-10 bg-[#f0f5f8] rounded-xl flex items-center justify-center text-xl">⚡</div>
                                         <h4 className="font-bold text-slate-800 uppercase tracking-tight text-sm">Analisi Bioenergetica</h4>
                                     </div>
                                     <p className="text-sm text-slate-600 leading-relaxed">
                                        Lavora sulla <strong>corazza muscolare</strong> e sul <strong>Grounding</strong> (radicamento), aiutando a sciogliere tensioni fisiche legate a blocchi emotivi.
                                     </p>
                                 </div>
                                 <div className="p-6 rounded-3xl border border-slate-100 bg-white shadow-sm hover:border-[#85a8bf]/30 hover:shadow-md transition">
                                     <div className="flex items-center gap-3 mb-4">
                                         <div className="w-10 h-10 bg-[#f0f5f8] rounded-xl flex items-center justify-center text-xl">⚖️</div>
                                         <h4 className="font-bold text-slate-800 uppercase tracking-tight text-sm">Analisi Transazionale</h4>
                                     </div>
                                     <p className="text-sm text-slate-600 leading-relaxed">
                                        Studia gli <strong>"Stati dell'Io"</strong> (Genitore, Adulto, Bambino) per analizzare e migliorare gli schemi relazionali inconsapevoli.
                                     </p>
                                 </div>
                             </div>
                        </section>

                        <section className="mb-12 p-8 bg-slate-50 rounded-3xl border border-slate-100">
                             <h2 className="text-2xl font-bold text-slate-800 mb-8 flex items-center gap-3 italic">
                                <span className="bg-[#335d73] text-white w-8 h-8 rounded-full flex items-center justify-center text-xs not-italic shadow-sm">3</span>
                                Gli Strumenti Creativi & Il Respiro
                             </h2>
                             <p className="text-lg text-[#335d73] mb-8 font-medium italic">"I ponti verso l'inconscio per dare forma al mondo interiore"</p>
                             
                             <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                 <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm border-t-4 border-t-[#85a8bf]">
                                     <div className="flex items-center gap-3 mb-3">
                                         <span className="text-2xl">🫁</span>
                                         <h4 className="text-xs font-black uppercase text-[#335d73] tracking-wider">Il Respiro</h4>
                                     </div>
                                     <p className="text-xs text-slate-500 leading-relaxed italic">Lo strumento centrale: tecniche per bilanciare l'energia e sentire il corpo vivo.</p>
                                 </div>
                                 <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                                     <div className="flex items-center gap-3 mb-3">
                                         <span className="text-2xl">✍️</span>
                                         <h4 className="text-xs font-black uppercase text-slate-700 tracking-wider">Scrittura</h4>
                                     </div>
                                     <p className="text-xs text-slate-500 leading-relaxed italic">Diario di bordo e lettere per oggettivare vissuti profondi.</p>
                                 </div>
                                 <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                                     <div className="flex items-center gap-3 mb-3">
                                         <span className="text-2xl">🎨</span>
                                         <h4 className="text-xs font-black uppercase text-slate-700 tracking-wider">Arte</h4>
                                     </div>
                                     <p className="text-xs text-slate-500 leading-relaxed italic">Colori e materiali per dare forma visibile all'invisibile.</p>
                                 </div>
                             </div>
                        </section>
                    </div>
                    
                    <div className="mt-12 flex justify-center">
                        <button onClick={() => setSelectedDocId(null)} className="px-8 py-4 bg-[#335d73] text-white rounded-2xl font-bold hover:opacity-90 transition shadow-lg">Torna alla Landing Page</button>
                    </div>
                </div>
            </div>
        );
    }

    if (selectedDocId === 'privacy') {
        return (
            <div className="min-h-screen bg-slate-50 p-6 flex flex-col items-center animate-fade-in relative overflow-hidden">
                <AmbientBackground />
                <div className="max-w-4xl w-full bg-white rounded-3xl shadow-xl p-8 md:p-12 relative z-10 my-8">
                    <button onClick={() => setSelectedDocId(null)} className="absolute top-6 right-8 text-sm font-bold text-slate-400 hover:text-slate-800 transition">✕ Chiudi</button>
                    
                    <div className="prose prose-slate max-w-none">
                        <div className="flex items-center gap-6 mb-12 border-b pb-6">
                             <div className="w-20 h-20 bg-[#f0f5f8] rounded-3xl flex items-center justify-center text-4xl shadow-sm">🔒</div>
                             <div>
                                <h1 className="text-3xl font-black text-[#24485d] m-0">Gestione Dati & Sicurezza</h1>
                                <p className="text-sm text-[#85a8bf] font-bold uppercase tracking-widest mt-1">Trasparenza e Protezione (GDPR)</p>
                             </div>
                        </div>

                        <section className="mb-12">
                            <h3 className="text-xl font-bold text-slate-800 mb-6">1. Chi gestisce i tuoi dati?</h3>
                            <p className="text-lg text-slate-700 leading-relaxed mb-6">
                                I tuoi dati sono sotto la responsabilità del tuo <strong>Counselor Professionista</strong>. Questa sezione ti spiega tecnicamente come vengono gestiti all'interno della piattaforma <strong>AwarenessTool</strong>, in conformità con l'informativa legale che verrebbe sottoscritta all'inizio di un eventuale percorso.
                            </p>
                        </section>

                        <section className="mb-12">
                            <h3 className="text-xl font-bold text-slate-800 mb-6">2. Infrastruttura Tecnologica</h3>
                            <p className="text-lg text-slate-700 leading-relaxed mb-6">
                                AwarenessTool utilizza l'infrastruttura <strong>Google Firebase / Firestore</strong>, uno standard globale di sicurezza certificato per l'archiviazione dati in conformità con il GDPR.
                            </p>
                            <div className="grid md:grid-cols-2 gap-6 mt-8">
                                <div className="p-8 bg-[#f0f5f8] rounded-3xl border border-[#d9e7ed] shadow-sm">
                                    <h4 className="font-bold text-[#335d73] text-sm mb-4 uppercase tracking-wide">Dati in Unione Europea</h4>
                                    <p className="text-sm text-[#436f89] leading-relaxed italic">Tutti i database sono situati in server all'interno dell'UE per garantire la massima protezione legale europea.</p>
                                </div>
                                <div className="p-8 bg-[#f0f5f8] rounded-3xl border border-[#d9e7ed] shadow-sm">
                                    <h4 className="font-bold text-[#335d73] text-sm mb-4 uppercase tracking-wide">Segretezza Professionale</h4>
                                    <p className="text-sm text-[#436f89] leading-relaxed italic">Solo tu e il tuo counselor potete visualizzare i contenuti dei tuoi questionari e del diario. Nessun altro ha accesso al database degli utenti nominativi.</p>
                                </div>
                            </div>
                        </section>
                    </div>

                    <div className="mt-12 flex justify-center">
                        <button onClick={() => setSelectedDocId(null)} className="px-8 py-4 bg-[#335d73] text-white rounded-2xl font-bold hover:opacity-90 transition shadow-lg">Torna alla Landing Page</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col animate-fade-in relative overflow-hidden bg-white">
            <AmbientBackground />
            
            {/* Header / Nav */}
            <nav className="relative z-20 px-8 py-6 flex justify-between items-center max-w-7xl mx-auto w-full">
                <div className="flex items-center gap-3">
                    <img src="/logo.png" alt="Logo" className="h-10 w-auto" />
                    <span className="text-xl font-black text-slate-800 tracking-tighter">AwarenessTool</span>
                </div>
                <button 
                    onClick={onBack}
                    className="px-6 py-2 border-2 border-slate-800 rounded-full text-sm font-bold hover:bg-slate-800 hover:text-white transition-all shadow-sm"
                >
                    Torna al Login
                </button>
            </nav>

            {/* Hero Section */}
            <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-12 text-center overflow-y-auto">
                <div className="max-w-4xl w-full">
                    <div className="inline-block px-4 py-1.5 bg-[#f0f5f8] border border-[#d9e7ed] rounded-full mb-8">
                        <span className="text-[10px] font-black text-[#335d73] uppercase tracking-[0.2em]">Spazio di Consapevolezza</span>
                    </div>
                    
                    <h1 className="text-5xl md:text-7xl font-black text-[#24485d] mb-8 leading-tight tracking-tight">
                        Ti incuriosisce,<br />
                        <span className="text-[#85a8bf] italic font-light">ma non sai se fa per te?</span>
                    </h1>
                    
                    <p className="text-xl text-slate-500 mb-12 max-w-2xl mx-auto leading-relaxed">
                        Inizia esplorando le basi del percorso e come proteggiamo lo spazio sacro della tua riflessione interiore.
                    </p>

                    {/* Feature Grid */}
                    <div className="grid md:grid-cols-3 gap-6 mb-16">
                        {docs.map(doc => (
                            <div 
                                key={doc.id}
                                onClick={() => {
                                    if (doc.id === 'somatic') {
                                        navigate('/bioenergeticamente-te');
                                    } else {
                                        setSelectedDocId(doc.id);
                                    }
                                }}
                                className={`group p-8 border-2 rounded-[2.5rem] hover:shadow-2xl transition-all duration-500 cursor-pointer text-left relative overflow-hidden ${
                                    doc.highlight
                                        ? 'bg-emerald-50 border-emerald-200 hover:bg-white hover:border-emerald-400 hover:shadow-emerald-100/60'
                                        : 'bg-slate-50 border-slate-50 hover:bg-white hover:border-[#85a8bf] hover:shadow-[#85a8bf]/20'
                                }`}
                            >
                                {doc.highlight && (
                                    <div className="absolute top-4 right-4">
                                        <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-600 text-white px-2.5 py-1 rounded-full">Prova Gratis</span>
                                    </div>
                                )}
                                <div className={`w-16 h-16 rounded-3xl flex items-center justify-center text-3xl mb-6 shadow-sm group-hover:scale-110 transition-transform duration-500 border ${
                                    doc.highlight ? 'bg-white border-emerald-100' : 'bg-white border-slate-100'
                                }`}>
                                    {doc.icon}
                                </div>
                                <h3 className="text-xl font-black text-[#24485d] mb-3">{doc.title}</h3>
                                <p className="text-slate-500 text-sm leading-relaxed mb-6">{doc.description}</p>
                                <div className={`flex items-center gap-2 font-bold text-sm ${
                                    doc.highlight ? 'text-emerald-700' : 'text-[#335d73]'
                                }`}>
                                    {doc.highlight ? 'Fai il test →' : 'Scopri di più'} <span className="text-xl group-hover:translate-x-1 transition-transform">→</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Contact Section */}
                    <div className="bg-[#335d73] rounded-[3rem] p-10 md:p-16 text-white shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#85a8bf]/20 blur-[100px] rounded-full -mr-32 -mt-32"></div>
                        <div className="relative z-10">
                            <h2 className="text-3xl font-black mb-4 italic">Hai domande o vuoi iniziare?</h2>
                            <p className="text-slate-200 mb-8 max-w-lg mx-auto opacity-80">
                                Se senti il bisogno di approfondire o vuoi fissare un primo colloquio conoscitivo, scrivici.
                            </p>
                            <a 
                                href="mailto:info@awarenesstool.it" 
                                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-[#335d73] rounded-2xl font-black hover:bg-[#f0f5f8] transition transform hover:-translate-y-1 shadow-lg"
                            >
                                📩 info@awarenesstool.it
                            </a>
                        </div>
                    </div>

                    <p className="mt-12 text-slate-400 text-xs font-medium uppercase tracking-widest pb-12">
                        Consapevolezza • Tecnologia • Cambiamento
                    </p>
                </div>
            </main>
        </div>
    );
};

export default ProspectLanding;
